import { applyAndCacheTheme } from '@/utils/theme';
import { defineStore } from "pinia";
import { request } from "@/utils/api";
import router from "@/router";
import { AUTH_TOKEN_STORAGE_KEY, AUTH_ROLE_STORAGE_KEY, AUTH_FIRST_NAME_STORAGE_KEY, AUTH_NEEDS_BROKERAGE_STORAGE_KEY, AUTH_DEVELOPER_STORAGE_KEY, AUTH_PASSWORD_DAYS_LEFT_STORAGE_KEY } from "@/utils/authStorage";
import { LoginResponse, UserRole } from "@/types/user";

export const useAuthStore = defineStore("auth", {
    state: () => {
        return {
            token: localStorage.getItem(AUTH_TOKEN_STORAGE_KEY) as string | null,
            email: null as string | null,
            role: localStorage.getItem(AUTH_ROLE_STORAGE_KEY) as UserRole | null,
            firstName: localStorage.getItem(AUTH_FIRST_NAME_STORAGE_KEY) as string | null,
            brokerageName: null as string | null,
            brokerageCount: 0,
            billingEnabled: false,
            seatChangesLocked: false,
            logoUrl: null as string | null,
            logoVersion: 0,
            isDeveloper: localStorage.getItem(AUTH_DEVELOPER_STORAGE_KEY) === 'true',
            needsBrokerage: localStorage.getItem(AUTH_NEEDS_BROKERAGE_STORAGE_KEY) === 'true',
            // Days until the password expires, only known (and set) when it is 14 or fewer.
            passwordDaysLeft: (localStorage.getItem(AUTH_PASSWORD_DAYS_LEFT_STORAGE_KEY) !== null
                ? Number(localStorage.getItem(AUTH_PASSWORD_DAYS_LEFT_STORAGE_KEY)) : null) as number | null,
            // The change-password dialog (opened from the user menu or the expiry warning).
            passwordDialogOpen: false,
            passwordWarningDismissed: false,
        };
    },
    getters: {
        isAuthenticated: (state) => !!state.token,
        isAdmin: (state) => state.role === 'ADMIN',
        isViewer: (state) => state.role === 'VIEWER',
        // Admins and regular users can add/edit; viewers can only look.
        canEdit: (state) => state.role === 'ADMIN' || state.role === 'USER',
    },
    actions: {
        async login(email: string, password: string): Promise<LoginResponse> {
            const response: LoginResponse = await request('POST', '/users/login', { email, password });

            if (response.status === 'verified' && response.token) {
                this.token = response.token;
                this.email = email;
                this.role = response.role ?? null;
                this.firstName = response.firstName ?? null;
                localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, response.token);
                if (response.role) {
                    localStorage.setItem(AUTH_ROLE_STORAGE_KEY, response.role);
                }
                if (response.firstName) {
                    localStorage.setItem(AUTH_FIRST_NAME_STORAGE_KEY, response.firstName);
                }
                this.setNeedsBrokerage(response.brokerageSelectionRequired === 'true');
                this.setPasswordDaysLeft(response.passwordDaysLeft !== undefined ? Number(response.passwordDaysLeft) : null);
            }

            return response;
        },
        setPasswordDaysLeft(days: number | null) {
            this.passwordDaysLeft = days !== null && !Number.isNaN(days) ? days : null;
            this.passwordWarningDismissed = false;
            if (this.passwordDaysLeft === null) {
                localStorage.removeItem(AUTH_PASSWORD_DAYS_LEFT_STORAGE_KEY);
            } else {
                localStorage.setItem(AUTH_PASSWORD_DAYS_LEFT_STORAGE_KEY, String(this.passwordDaysLeft));
            }
        },
        setNeedsBrokerage(value: boolean) {
            this.needsBrokerage = value;
            if (value) {
                localStorage.setItem(AUTH_NEEDS_BROKERAGE_STORAGE_KEY, 'true');
            } else {
                localStorage.removeItem(AUTH_NEEDS_BROKERAGE_STORAGE_KEY);
            }
        },
        /** Who is signed in and for which brokerage (name, role, whether they may switch). */
        async loadSession() {
            const session = await request('GET', '/users/session');
            this.brokerageCount = session.brokerageCount ?? 0;
            this.isDeveloper = !!session.developer;
            this.billingEnabled = !!session.billingEnabled;
            this.seatChangesLocked = !!session.seatChangesLocked;
            localStorage.setItem(AUTH_DEVELOPER_STORAGE_KEY, String(this.isDeveloper));
            if (session.tenantId != null) {
                this.brokerageName = session.brokerageName ?? null;
                applyAndCacheTheme(session.theme ?? {});
                if ((session.logoVersion ?? 0) !== this.logoVersion || (session.logoVersion && !this.logoUrl)) {
                    this.logoVersion = session.logoVersion ?? 0;
                    this.loadLogo().catch(() => undefined);
                }
                if (session.role) {
                    this.role = session.role;
                    localStorage.setItem(AUTH_ROLE_STORAGE_KEY, session.role);
                }
                this.setNeedsBrokerage(false);
                if (session.billingLocked && router.currentRoute.value.name !== 'abonnement') {
                    router.push({ name: 'abonnement' });
                }
            }
        },
        /** The brokerage logo as an object URL (an <img> cannot send the login token, so it is fetched here). */
        async loadLogo() {
            if (this.logoUrl) {
                URL.revokeObjectURL(this.logoUrl);
                this.logoUrl = null;
            }
            try {
                const blob = await request('GET', '/brokerage/logo', undefined, { responseType: 'blob' });
                this.logoUrl = URL.createObjectURL(blob);
            } catch (e) {
                this.logoUrl = null; // no logo uploaded
            }
        },
        async selectBrokerage(tenantId: number) {
            await request('POST', '/users/select-brokerage', { tenantId });
            this.setNeedsBrokerage(false);
            await this.loadSession();
        },
        async logout() {
            try {
                await request('POST', '/users/logout');
            } catch (error) {
                // Token may already be invalid/expired server-side - clearing the
                // local session is the important part either way.
                console.warn('Logout request failed, clearing local session anyway', error);
            }

            this.token = null;
            this.email = null;
            this.role = null;
            this.firstName = null;
            this.brokerageName = null;
            this.brokerageCount = 0;
            this.isDeveloper = false;
            this.needsBrokerage = false;
            this.logoUrl = null;
            this.logoVersion = 0;
            this.setPasswordDaysLeft(null);
            this.passwordDialogOpen = false;
            localStorage.removeItem(AUTH_NEEDS_BROKERAGE_STORAGE_KEY);
            localStorage.removeItem(AUTH_DEVELOPER_STORAGE_KEY);
            localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
            localStorage.removeItem(AUTH_ROLE_STORAGE_KEY);
            localStorage.removeItem(AUTH_FIRST_NAME_STORAGE_KEY);
        },
    },
})
